import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import JobCardItem from './JobCardItem';
import JobsGallery from './JobsGallery';
import { deleteJob, loadUserJobs, saveJob } from './utilities/jobsUtils';
import FeedbackNotice from '../Common/Feedback/FeedbackNotice';

const DesiredJobsManager = ({ userId, onJobsLoaded }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [category, setCategory] = useState('');

  const loadJobs = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const loadedJobs = (await loadUserJobs(userId)) || [];
      setJobs(loadedJobs);
      onJobsLoaded?.(loadedJobs);
    } catch (err) {
      console.error('Error loading desired jobs:', err);
      setError('Failed to load desired jobs');
    } finally {
      setLoading(false);
    }
  }, [onJobsLoaded, userId]);

  useEffect(() => { void loadJobs(); }, [loadJobs]);

  const handleSelectJob = async (job) => {
    try {
      await saveJob(userId, {
        title: job.title, description: job.description, uri: job.uri, code: job.code,
        type: job.type, category: job.category,
        essentialSkillsAndCompetences: job.essentialSkillsAndCompetences || [],
        essentialKnowledge: job.essentialKnowledge || [],
        optionalKnowledge: job.optionalKnowledge || [],
        optionalSkillsAndCompetences: job.optionalSkillsAndCompetences || [],
      });
      setSuccessMessage(`Job "${job.title}" added successfully`);
      await loadJobs();
    } catch (err) {
      console.error('Error saving desired job:', err);
      setError('Failed to save selected job');
    }
  };

  const handleDeleteJob = async (jobId, jobTitle) => {
    if (!window.confirm(`Are you sure you want to remove "${jobTitle}"?`)) return;
    try {
      await deleteJob(userId, jobId);
      setSuccessMessage('Desired job removed successfully');
      await loadJobs();
    } catch (err) {
      console.error('Error deleting desired job:', err);
      setError('Failed to remove desired job');
    }
  };

  return (
    <div className="px-3 px-md-0" role="tabpanel" aria-label="Desired jobs">
      <FeedbackNotice message={error || ''} variant="danger" onDismiss={() => setError(null)} />
      <FeedbackNotice message={successMessage || ''} variant="success" onDismiss={() => setSuccessMessage(null)} />
      <div className="d-flex align-items-center justify-content-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="h4 mb-1">Your desired jobs</h2>
          <p className="text-muted mb-0">These roles guide the AI's emphasis and wording; they are never presented as your work history.</p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input type="text" className="form-control" style={{ width: '220px' }} value={category}
            onChange={(event) => setCategory(event.target.value)} placeholder="Default category" aria-label="Default job category" />
          <button className="btn btn-primary" onClick={() => setShowGallery(true)} disabled={!userId}>
            <i className="bi bi-plus-lg me-2" aria-hidden="true"></i>Select Job
          </button>
        </div>
      </div>
      {loading ? (
        <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
      ) : jobs.length === 0 ? (
        <div className="alert alert-info rounded-3 p-4 text-center">
          <h3 className="h5 alert-heading mb-2">No desired jobs selected yet</h3>
          <p className="mb-3">Choose roles you want to pursue so the AI can tailor your portfolio to relevant strengths.</p>
          <button className="btn btn-primary" onClick={() => setShowGallery(true)}><i className="bi bi-search me-2" aria-hidden="true"></i>Open Jobs Gallery</button>
        </div>
      ) : (
        <div className="row g-3">{jobs.map((job) => <div key={job.id} className="col-12 col-md-6 col-xl-4"><JobCardItem job={job} onDelete={handleDeleteJob} /></div>)}</div>
      )}
      {showGallery ? <JobsGallery onSelect={handleSelectJob} onClose={() => setShowGallery(false)} defaultCategory={category} /> : null}
    </div>
  );
};

export default DesiredJobsManager;

DesiredJobsManager.propTypes = {
  userId: PropTypes.string,
  onJobsLoaded: PropTypes.func,
};
